import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { withRetry } from '../lib/query'

/**
 * RUNNING THE TEAM WITHOUT THE SQL EDITOR
 * ---------------------------------------
 * Until today, adding a field officer meant opening the Supabase SQL editor and
 * writing an INSERT. That is not a thing a supervisor will ever do, which meant
 * the system could only ever have one user — and a system with one user cannot
 * be tested by anyone except the person who built it.
 *
 * DELIBERATELY ONLINE-ONLY. Every other screen in this app reads and writes the
 * phone first, because officers work in villages with no signal. This one does
 * the opposite: it talks straight to the server and says so when it cannot.
 * Team administration happens in an office, once, by one person, and caching it
 * would mean two administrators on two laptops could each queue a change that
 * silently overwrites the other's — with role assignments, which decide who can
 * see household data. Offline is the right default; this is the exception that
 * proves it was a choice.
 */

type Member = {
  user_id: string
  email: string | null
  full_name: string | null
  role_code: string
  role_name: string
  role_rank: number
  is_active: boolean
  village_count: number
  village_names: string | null
  created_at: string
}

type Role = { role_code: string; name_en: string; name_km: string | null; rank: number }
type Village = { village_id: string; code: string; name_en: string | null }

export function TeamScreen({ myUserId, onBack }: { myUserId: string; onBack: () => void }) {
  const [members, setMembers] = useState<Member[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [villages, setVillages] = useState<Village[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [open, setOpen] = useState<Member | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)

    const [team, r, v] = await Promise.all([
      withRetry(() => supabase.rpc('team_members')),
      withRetry(() => supabase.from('roles').select('role_code, name_en, name_km, rank').order('rank')),
      withRetry(() =>
        supabase.from('villages').select('village_id, code, name_en').order('code').limit(2000),
      ),
    ])

    if (team.error) setError(team.error.message)
    else setMembers((team.data ?? []) as Member[])
    setRoles((r.data ?? []) as Role[])
    setVillages((v.data ?? []) as Village[])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (open) {
    return (
      <MemberScreen
        member={open}
        roles={roles}
        villages={villages}
        isMe={open.user_id === myUserId}
        onBack={() => setOpen(null)}
        onSaved={(msg) => {
          setOpen(null)
          setNotice(msg)
          load()
        }}
      />
    )
  }

  const waiting = members.filter((m) => !m.is_active)
  const active = members.filter((m) => m.is_active)

  return (
    <div className="screen">
      <div className="appbar">
        <h1>Team</h1>
        <button className="btn-quiet" onClick={onBack}>
          Back
        </button>
      </div>

      <div className="content">
        {notice && (
          <div
            className="alert"
            style={{
              borderColor: 'var(--green-700)',
              color: 'var(--green-900)',
              background: 'var(--green-100)',
            }}
          >
            {notice}
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            {error}
            <div className="muted" style={{ marginTop: 6 }}>
              This screen needs a connection. It is the one part of the app that
              does, because who may see household data is not a decision to make
              from a stale copy.
            </div>
          </div>
        )}

        {loading && <p className="spinner">Loading the team…</p>}

        {/* Anyone waiting is the most urgent thing on this screen: somebody has
            created a login and cannot do anything until they are let in. */}
        {waiting.length > 0 && (
          <>
            <h3>Waiting to be let in ({waiting.length})</h3>
            <p className="muted" style={{ marginTop: -8 }}>
              These logins exist but are switched off. They can see nothing at
              all until you activate them.
            </p>
            <ul className="list">
              {waiting.map((m) => (
                <li key={m.user_id} style={{ padding: 0 }}>
                  <MemberRow m={m} onOpen={() => setOpen(m)} />
                </li>
              ))}
            </ul>
          </>
        )}

        <h3 style={{ marginTop: waiting.length ? 24 : 0 }}>Active ({active.length})</h3>
        <ul className="list">
          {active.map((m) => (
            <li key={m.user_id} style={{ padding: 0 }}>
              <MemberRow m={m} isMe={m.user_id === myUserId} onOpen={() => setOpen(m)} />
            </li>
          ))}
        </ul>

        <div className="card" style={{ maxWidth: 'none', marginTop: 24, marginBottom: 40 }}>
          <h3 style={{ marginTop: 0 }}>Adding somebody new</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            The login itself is created in the Supabase dashboard —{' '}
            <strong>Authentication → Users → Add user</strong> — with their email
            and a temporary password. It appears here within seconds, switched
            off. Then give them a name, a role and a village, and activate them.
          </p>
          <p className="muted" style={{ marginBottom: 0 }}>
            It is not done in this app on purpose. Creating a login needs a key
            that can read every household in the country, and that key must
            never be inside something installed on a phone.
          </p>
        </div>
      </div>
    </div>
  )
}

function MemberRow({ m, isMe, onOpen }: { m: Member; isMe?: boolean; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        border: 0,
        padding: 14,
      }}
    >
      <div className="name">
        {m.full_name || m.email || '(no name)'}
        {isMe && <span className="muted"> · you</span>}
      </div>
      <div className="code">{m.email ?? '(no email on record)'}</div>
      <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <span className="pill">{m.role_name}</span>
        {m.village_count > 0 && <span className="pill">{m.village_names}</span>}
        {m.role_rank < 30 && m.village_count === 0 && (
          <span className="pill pill-waiting">no village yet</span>
        )}
      </div>
    </button>
  )
}

// -----------------------------------------------------------------------------
//  ONE PERSON
// -----------------------------------------------------------------------------

function MemberScreen({
  member,
  roles,
  villages,
  isMe,
  onBack,
  onSaved,
}: {
  member: Member
  roles: Role[]
  villages: Village[]
  isMe: boolean
  onBack: () => void
  onSaved: (message: string) => void
}) {
  const [fullName, setFullName] = useState(member.full_name ?? '')
  const [roleCode, setRoleCode] = useState(member.role_code)
  const [assigned, setAssigned] = useState<Set<string>>(new Set())
  const [loadingAssignments, setLoadingAssignments] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    supabase
      .from('user_village_assignments')
      .select('village_id')
      .eq('user_id', member.user_id)
      .then(({ data }) => {
        setAssigned(new Set((data ?? []).map((r: { village_id: string }) => r.village_id)))
        setLoadingAssignments(false)
      })
  }, [member.user_id])

  // Ranks 30 and above (MERL officer, manager, administrator) see every village
  // and need no assignment rows at all — migration 003's design.
  const rank = roles.find((r) => r.role_code === roleCode)?.rank ?? 10
  const needsVillages = rank < 30

  async function toggleVillage(villageId: string, on: boolean) {
    setError(null)
    const fn = on ? 'assign_village' : 'unassign_village'
    const { error } = await supabase.rpc(fn, {
      p_user_id: member.user_id,
      p_village_id: villageId,
    })
    if (error) {
      setError(error.message)
      return
    }
    setAssigned((s) => {
      const next = new Set(s)
      if (on) next.add(villageId)
      else next.delete(villageId)
      return next
    })
  }

  async function save(activate?: boolean) {
    setError(null)
    setBusy(true)
    const { error } = await supabase.rpc('update_team_member', {
      p_user_id: member.user_id,
      p_full_name: fullName.trim() || null,
      p_role_code: roleCode,
      p_is_active: activate ?? null,
    })
    setBusy(false)

    if (error) {
      setError(error.message)
      return
    }
    onSaved(
      activate === true
        ? `${fullName || member.email} can now sign in.`
        : activate === false
          ? `${fullName || member.email} has been switched off. Their records and history stay exactly where they are.`
          : 'Saved.',
    )
  }

  const selectStyle = {
    width: '100%',
    minHeight: 48,
    fontSize: 16,
    borderRadius: 10,
    border: '2px solid var(--line)',
    padding: '10px 12px',
  }

  return (
    <div className="screen">
      <div className="appbar">
        <h1>{member.full_name || member.email || 'Team member'}</h1>
        <button className="btn-quiet" onClick={onBack}>
          Back
        </button>
      </div>

      <div className="content">
        <div className="card" style={{ maxWidth: 'none' }}>
          <div className="muted">{member.email}</div>

          <label htmlFor="nm" style={{ marginTop: 14 }}>
            Full name
          </label>
          <input id="nm" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          <p className="muted" style={{ marginTop: 4 }}>
            This is the name that appears in every change history, so write it
            the way the team would say it.
          </p>

          <label htmlFor="rl">Role</label>
          <select id="rl" value={roleCode} onChange={(e) => setRoleCode(e.target.value)} style={selectStyle}>
            {roles.map((r) => (
              <option key={r.role_code} value={r.role_code}>
                {r.name_en}
              </option>
            ))}
          </select>
          <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
            {roleCode === 'manager' &&
              'A programme manager sees counts and coverage only — never a household row. That is deliberate, and it is what "no personal data by default" means here.'}
            {roleCode === 'admin' &&
              'An administrator can manage everyone, including you. Give this out sparingly.'}
            {roleCode === 'field_officer' &&
              'A field officer can register and correct households, in their assigned villages only.'}
            {roleCode === 'supervisor' &&
              'A supervisor can review and void observations in their assigned villages.'}
            {roleCode === 'merl_officer' &&
              'A MERL officer can read every village, and cannot register households.'}
          </p>
        </div>

        {needsVillages && (
          <div className="card" style={{ maxWidth: 'none', marginTop: 14 }}>
            <h3 style={{ marginTop: 0 }}>Villages</h3>
            <p className="muted" style={{ marginTop: 0 }}>
              This person can only see households in the villages ticked here. An
              officer with no village sees an empty app and usually assumes it is
              broken.
            </p>

            {loadingAssignments && <p className="spinner">Loading…</p>}

            {!loadingAssignments &&
              villages.slice(0, 60).map((v) => {
                const on = assigned.has(v.village_id)
                return (
                  <button
                    key={v.village_id}
                    onClick={() => toggleVillage(v.village_id, !on)}
                    className={on ? 'btn-primary' : ''}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: 8,
                      ...(on ? {} : { border: '2px solid var(--line)', background: 'var(--white)' }),
                    }}
                  >
                    {on ? '✓ ' : ''}
                    {v.name_en ?? v.code} · {v.code}
                  </button>
                )
              })}

            {villages.length > 60 && (
              <p className="muted">
                Showing the first 60 of {villages.length} villages. Search comes
                with the multi-village stage.
              </p>
            )}
          </div>
        )}

        {!needsVillages && (
          <div className="alert" style={{ borderColor: 'var(--line)', marginTop: 14 }}>
            This role sees every village, so there is nothing to assign.
          </div>
        )}

        {error && (
          <div className="alert alert-error" style={{ marginTop: 14 }}>
            {error}
          </div>
        )}

        <div style={{ margin: '18px 0 40px' }}>
          <button className="btn-primary" onClick={() => save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save name and role'}
          </button>

          {!member.is_active && (
            <button
              className="btn-primary"
              style={{ marginTop: 12 }}
              onClick={() => save(true)}
              disabled={busy}
            >
              Activate — let them sign in
            </button>
          )}

          {member.is_active && !isMe && (
            <button
              style={{ marginTop: 12, border: '2px solid var(--danger)', color: 'var(--danger)', background: 'var(--white)' }}
              onClick={() => save(false)}
              disabled={busy}
            >
              Switch off this account
            </button>
          )}

          {member.is_active && isMe && (
            <p className="muted" style={{ marginTop: 12 }}>
              You cannot switch off your own account. Another administrator has
              to do that — which also leaves a record of who decided it.
            </p>
          )}

          <p className="muted" style={{ marginTop: 14 }}>
            Switching an account off never deletes anything. Every household they
            registered keeps their name against it, which is the whole reason
            people are retired rather than removed.
          </p>
        </div>
      </div>
    </div>
  )
}
